import { makeAutoObservable, runInAction } from 'mobx'
import { AxiosError } from 'axios'
import { debounce } from 'lodash'
import { type IButtonProps, type IDropdownItem, type ITabItem } from 'shared/ui'
import { DayjsFormats, logger, numberFormat, sendGTMEvent } from 'shared/lib'
import { uiStore } from 'shared/store/uiStore'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { ZIndex } from 'shared/constants/zIndex'
import { type IEntitiesStore } from 'entities/EntitiesExport'
import { type BillingPlan } from 'entities/Billing/model/BillingPlan'
import { openIntercom } from 'entities/Intercom'
import { AutoRechargeWidgetStore } from 'entities/Billing/ui/AutoRechargeModal'
import { DropdownCardsStore } from 'entities/Billing/ui/DropdownCards'
import { BillingApi } from 'entities/Billing'
import { type IResponseGetBillingPreview } from 'entities/Billing/api/types'
import { BillingPath } from 'entities/ExportRouters'
import { QuestionnaireOrSelectNumberModalStore } from 'entities/Phone/ui/QuestionnaireOrSelectNumberModal'
import { type IPlan } from 'pages/settings/ui/BillingPlan/ui/PlanContainer'
import { PlanSelectionStore } from 'pages/settings/ui/BillingPlan/store/planSelectionStore'
import { DowngradeContentStore } from 'pages/settings/pages/billing/pages/upgrade/store/downgradeContentStore'
import { OpportunityModal } from 'pages/settings/pages/billing/pages/overview/ui/OpportunityModal/ui/OpportunityModal'
import type { BillingPeriod } from 'pages/settings/ui/BillingPlan'

export type IUpgradePageLocationState = {
  fromSuggestion?: boolean
  period?: BillingPeriod
  checkOpportunity?: boolean
}

const brandedUrlShortenerPrice = 20
const brandedUrlShortenerPriceYearly = 240
const trustedCallingPrice = 10
const trustedCallingPriceYearly = 120

const seatsPrice = 10
const seatsPriceYearly = 100
const numbersPrice = 5
const numbersPriceYearly = 50

export class BillingUpgradePageStore {
  private _fromSuggestion = false
  private _checkOpportunity = false

  private _currentStep = 1
  private readonly _totalSteps = 2
  private _selectedPlanType: 'Basic' | 'Pro' | null = null
  private _selectedPlan: BillingPlan | null = null
  private _seats = 0
  private _numbers = 0
  private _autoRechargeWidgetStore: AutoRechargeWidgetStore
  private _dropdownCardsStore: DropdownCardsStore
  private _downgradeContentStore: DowngradeContentStore
  private _planSelectionStore: PlanSelectionStore
  private _planData: IResponseGetBillingPreview | null = null
  private _paymentError = false
  private _isUpgrading = false
  private _showAutoRechargeBlock = false
  private _showPhoneCheckrBlock = false
  private _planLoading = false
  private _debouncedLoadPlanData: () => void

  constructor(private _entities: IEntitiesStore, locationState?: IUpgradePageLocationState | null) {
    this._autoRechargeWidgetStore = new AutoRechargeWidgetStore(this._entities.billingStore, {
      zIndex: ZIndex.OVERLAY_BASE_500,
    })
    this._dropdownCardsStore = new DropdownCardsStore(this._entities.billingStore)
    this._downgradeContentStore = new DowngradeContentStore()
    this._planSelectionStore = new PlanSelectionStore(this._entities, {
      getSeatsUnitPrice: () => this.seatsUnitPrice,
      getNumbersUnitPrice: () => this.numbersUnitPrice,
    })
    this._debouncedLoadPlanData = debounce(this.loadPlanData, 500)

    if (locationState?.fromSuggestion) {
      this._fromSuggestion = locationState.fromSuggestion
    }
    if (locationState?.period) {
      this._planSelectionStore.setPeriod(locationState.period)
    }
    if (locationState?.checkOpportunity) {
      this._checkOpportunity = locationState.checkOpportunity
    }

    makeAutoObservable(this)
  }

  init = () => {
    this._entities.billingStore.fetchBillingComplianceInfo()
    if (!this._entities.shortLinkStore.customDomain) {
      this._entities.shortLinkStore.loadCustomDomain()
    }

    this.setDefaultValues()
    this.checkAddOnsShowing()
    this.checkFromSuggestion()
    this.checkOpportunity()
    this.sendOpenUpdateAnalyticsEvent()
  }

  private checkOpportunity = () => {
    if (this._checkOpportunity) {
      this._checkOpportunity = false
      this.openOpportunityModal()
      return
    }

    if (
      !this._entities.featureFlagsStore.billing_overview_v2 &&
      this._entities.subscriptionStore.subscriptionSuggestion
    ) {
      this.openOpportunityModal()
    }
  }

  openOpportunityModal = () => {
    modalStore.addModal({
      id: 'opportunityModal',
      title: 'Upgrade',
      showCloseButton: false,
      disabledOverlayClose: true,
      onClose: () => {
        modalStore.removeModal('opportunityModal')
      },
      width: '768px',
      ModalContent: OpportunityModal,
      ModalContentProps: {
        onUpgradeSuggestedPlan: this.handleUpgradeSuggestedPlan,
      },
    })
  }

  handleUpgradeSuggestedPlan = (period: BillingPeriod) => {
    modalStore.closeModal('opportunityModal')
    this._fromSuggestion = true
    this._planSelectionStore.setPeriod(period)
    this.applySelectedPeriodSuggestedPlan()
    this.nextStep()
    this.loadPlanData()
  }

  sendOpenUpdateAnalyticsEvent = () => {
    const { eventLogStore } = this._entities
    const { organizationStore, usersStore } = this._entities

    const eventData = {
      event_id: 'upgrade_modal_used',
      action: 'Modal Opened',
      agencyId: organizationStore.agency_id,
      userId: usersStore.user?.id,
      'Modal Version': 'V2.1',
      context: {
        groupId: organizationStore.id,
      },
    }

    eventLogStore.logEvent('Upgrade Modal Used', eventData, { groupId: organizationStore.id })
  }

  private checkFromSuggestion = () => {
    if (this._fromSuggestion && this.suggestedPlan) {
      this._fromSuggestion = false
      this.applySelectedPeriodSuggestedPlan()
      this.nextStep()
      this.loadPlanData()
    }
  }

  get subscriptionPlan() {
    const { subscriptionStore } = this._entities

    return subscriptionStore.plan
  }

  get subscriptionPlanCredits() {
    const { subscriptionStore } = this._entities

    return subscriptionStore.planCredits
  }

  get subscriptionPlanPriceDollars() {
    const { subscriptionStore } = this._entities

    return subscriptionStore.plan?.price
  }

  get basicPlansDropdownItems(): IDropdownItem[] {
    return this._planSelectionStore.basicPlansDropdownItems
  }

  get proPlansDropdownItems(): IDropdownItem[] {
    return this._planSelectionStore.proPlansDropdownItems
  }

  get fromSuggestion() {
    return this._fromSuggestion
  }

  set fromSuggestion(value: boolean) {
    this._fromSuggestion = value
  }

  get paymentError() {
    return this._paymentError
  }

  get isUpgrading() {
    return this._isUpgrading
  }

  get currentStep() {
    return this._currentStep
  }

  get totalSteps() {
    return this._totalSteps
  }

  get period() {
    return this._planSelectionStore.period
  }

  get isFirstStep() {
    return this._currentStep === 1
  }

  get isMonthly() {
    return this._planSelectionStore.isMonthly
  }

  get selectedValues() {
    return this._planSelectionStore.selectedValues
  }

  get selectedBasicPlan(): BillingPlan | null {
    return this._planSelectionStore.selectedBasicPlan
  }

  get selectedProPlan(): BillingPlan | null {
    return this._planSelectionStore.selectedProPlan
  }

  get selectedPlanType() {
    return this._selectedPlanType
  }

  get selectedPlan() {
    return this._selectedPlan
  }

  get selectedPlanInfo() {
    if (!this._selectedPlanType) {
      return null
    }

    const currentPeriodPlan =
      this._selectedPlanType === 'Basic' ? this.selectedBasicPlan : this.selectedProPlan

    if (!currentPeriodPlan) {
      return null
    }

    const planInfo = this._entities.billingStore.billingPlansStore.getPlanInfo(
      currentPeriodPlan.id,
      currentPeriodPlan.interval
    )

    if (!planInfo) {
      return null
    }

    return {
      id: currentPeriodPlan.id,
      icon: planInfo.icon,
      name: currentPeriodPlan.labelCreditsByIntervalAndPlanType,
      iconColor: planInfo.iconColor,
      credits: currentPeriodPlan.credits,
      additionalCreditPrice: currentPeriodPlan.additional_credit_price,
      interval: currentPeriodPlan.interval,
    }
  }

  get seatsUnitPrice(): number {
    return (this._planData?.seats.unit_price || 0) / 100
  }

  get numbersUnitPrice(): number {
    return (this._planData?.numbers.unit_price || 0) / 100
  }

  get additionalSeatPrice(): string {
    return this._planSelectionStore.additionalSeatPrice
  }

  get additionalNumbersPrice(): string {
    return this._planSelectionStore.additionalNumbersPrice
  }

  get annualSavings(): number {
    return this._planSelectionStore.getAnnualSavings(this._selectedPlanType)
  }

  get seats() {
    return this._seats
  }

  get numbers() {
    return this._numbers
  }

  get dropdownCardsStore() {
    return this._dropdownCardsStore
  }

  get downgradeContentStore() {
    return this._downgradeContentStore
  }

  get autoRechargeWidgetStore() {
    return this._autoRechargeWidgetStore
  }

  get periodSuffix() {
    const prefix = this.isMonthly ? ' / month' : ' / year'
    if (this.isSpecialSubscriptionState) {
      return prefix
    }

    if (!this.isPeriodChanged) {
      return ''
    }

    return prefix
  }

  get planLines() {
    const formatPrice = (val: number) =>
      numberFormat({
        value: val,
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }) + this.periodSuffix

    const getLineAmount = (key: string) => {
      const planData = this.isDowngrade
        ? this._planData?.next_charge_details?.lines
        : this._planData?.lines

      return planData?.find((line) => line.key === key)?.amount
    }

    const planValue = getLineAmount('plan')
    const seatsNewValue = getLineAmount('seat') || 0
    const numbersNewValue = getLineAmount('inbox') || 0

    const isCurrentMonthly = this.subscriptionPlan?.interval === 'monthly'
    const isSelectedMonthly = this.isMonthly
    const isSamePeriod =
      (isCurrentMonthly && isSelectedMonthly) || (!isCurrentMonthly && !isSelectedMonthly)

    const seatsTotalCount = (this._planData?.seats.total_count || 0) + 1
    const numbersTotalCount = (this._planData?.numbers.total_count || 0) + 1

    const lines = []

    if (planValue) {
      lines.push({ title: 'Plan', value: formatPrice(planValue / 100) })
    }

    const getSeatsTotalValue = () => {
      if (!this.isPeriodChanged && this.seats === 0 && this.isNormalActiveSubscription) {
        return 0
      }

      return (seatsNewValue + (this._planData?.seats.unit_price || 0)) / 100
    }

    lines.push({
      title: `Seats × ${seatsTotalCount}`,
      value: formatPrice(getSeatsTotalValue()),
    })

    const getNumbersTotalValue = () => {
      if (!this.isPeriodChanged && this.numbers === 0 && this.isNormalActiveSubscription) {
        return 0
      }

      return (numbersNewValue + (this._planData?.numbers.unit_price || 0)) / 100
    }

    lines.push({
      title: `Numbers × ${numbersTotalCount}`,
      value: formatPrice(getNumbersTotalValue()),
    })

    const trustedCallingValue = this.isMonthly ? trustedCallingPrice : trustedCallingPriceYearly
    const showZeroValue = isSamePeriod && !this._entities.subscriptionStore.isCanceled

    if (this._entities.billingStore.billingComplianceInfo?.subscribed) {
      lines.push({
        title: 'Trusted Calling',
        value: formatPrice(showZeroValue ? 0 : trustedCallingValue),
      })
    }

    const brandedUrlPrice = this.isMonthly
      ? brandedUrlShortenerPrice
      : brandedUrlShortenerPriceYearly

    const { isActiveCustomDomain } = this._entities.shortLinkStore

    if (isActiveCustomDomain) {
      lines.push({
        title: 'Branded URL Shortener',
        value: formatPrice(showZeroValue ? 0 : brandedUrlPrice),
      })
    }

    return lines
  }

  get totalDueToday() {
    if (this.isDowngrade) {
      return 0
    }

    return (this._planData?.prorated_amount || 0) / 100
  }

  get isNormalActiveSubscription() {
    return (
      !this._entities.subscriptionStore.isCanceled &&
      !this._entities.subscriptionStore.isTrial &&
      !this._entities.subscriptionStore.isPAYGPlan
    )
  }

  get shouldHideSeatsDiscount() {
    if (this.isSpecialSubscriptionState) {
      return false
    }

    return !this.isPeriodChanged && this.seats === 0
  }

  get shouldHideNumbersDiscount() {
    if (this.isSpecialSubscriptionState) {
      return false
    }

    return !this.isPeriodChanged && this.numbers === 0
  }

  get subtotalValue() {
    let subtotal = (this._planData?.remaining_amount || 0) / 100

    const seatsDiscount = (this._planData?.seats.unit_price || 0) / 100
    const numbersDiscount = (this._planData?.numbers.unit_price || 0) / 100

    if (!this.shouldHideSeatsDiscount && seatsDiscount !== 0) {
      subtotal += seatsDiscount
    }

    if (!this.shouldHideNumbersDiscount && numbersDiscount !== 0) {
      subtotal += numbersDiscount
    }

    return subtotal
  }

  get isSwitchingFromMonthlyToAnnual() {
    return this.subscriptionPlan?.interval === 'monthly' && this.period === 'annually'
  }

  get periodChangeDirection(): 'monthly-to-annual' | 'annual-to-monthly' | null {
    const currentInterval = this.subscriptionPlan?.interval
    if (!currentInterval) {
      return null
    }

    const isCurrentMonthly = currentInterval === 'monthly'
    const isSelectedMonthly = this.period === 'monthly'

    if (isCurrentMonthly && !isSelectedMonthly) {
      return 'monthly-to-annual'
    }

    if (!isCurrentMonthly && isSelectedMonthly) {
      return 'annual-to-monthly'
    }

    return null
  }

  get isPeriodChanged(): boolean {
    return this.periodChangeDirection !== null
  }

  get totalPlanData() {
    const lines: Array<{ title: string; value: number; color?: string }> = [
      {
        title: 'Subtotal',
        value: this.subtotalValue,
      },
    ]

    if (!this.shouldHideSeatsDiscount) {
      lines.push({
        title: 'Discount: Seats',
        value: -(this._planData?.seats.unit_price || 0) / 100,
        color: 'var(--green-80)',
      })
    }

    if (!this.shouldHideNumbersDiscount) {
      lines.push({
        title: 'Discount: Numbers',
        value: -(this._planData?.numbers.unit_price || 0) / 100,
        color: 'var(--green-80)',
      })
    }

    if (!this.isDowngrade) {
      lines.push({
        title: 'Unused time',
        value: (this._planData?.credit_amount || 0) / 100,
      })
    }
    const taxRates = this.isDowngrade
      ? this._planData?.next_charge_details?.tax_rates?.[0]
      : this._planData?.tax_rates?.[0]

    if (taxRates) {
      const taxesAmount = this.isDowngrade
        ? this._planData?.next_charge_details?.taxes_amount
        : this._planData?.taxes_amount

      lines.push({
        title: `Sales tax - ${taxRates.state} (${taxRates.percent}%)`,
        value: (taxesAmount || 0) / 100,
      })
    }

    return lines
  }

  private getPlanComparison = (
    selectedPlan: BillingPlan | null
  ): 'upgrade' | 'downgrade' | 'current' => {
    if (!selectedPlan) {
      return 'upgrade'
    }

    const currentPriceDollars = this.subscriptionPlanPriceDollars
    if (!currentPriceDollars) {
      return 'upgrade'
    }

    if (
      selectedPlan.price_dollars === currentPriceDollars &&
      selectedPlan.interval === this.subscriptionPlan?.interval
    ) {
      return 'current'
    }

    return selectedPlan.price_dollars > currentPriceDollars ? 'upgrade' : 'downgrade'
  }

  get isDowngrade() {
    return this._planData?.is_downgrade || false
  }

  get isSpecialSubscriptionState() {
    const { subscriptionStore } = this._entities
    return (
      this.isDowngrade ||
      subscriptionStore.isCanceled ||
      subscriptionStore.isTrial ||
      subscriptionStore.isPAYGPlan
    )
  }

  get downgradeEffectiveDate() {
    if (!this._planData?.next_charge_details?.date) {
      return null
    }
    return uiStore.dayjs(this._planData.next_charge_details.date).format(DayjsFormats.full6)
  }

  get nextChargeAmountDollars(): string | null {
    if (this.isDowngrade || !this._planData?.next_charge_details?.total) {
      return null
    }

    return numberFormat({
      value: (this._planData.next_charge_details.total ?? 0) / 100,
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  }

  get planData() {
    return this._planData
  }

  private getButtonPropsForPlan = (
    plan: BillingPlan | null,
    isPro: boolean
  ): Partial<IButtonProps> => {
    const isCanceled = this._entities.subscriptionStore.isCanceled
    const comparison = this.getPlanComparison(plan)

    if (comparison === 'current' && !isCanceled && !this._entities.subscriptionStore.isTrial) {
      return {
        text: 'Current plan',
        disabled: true,
        contained: 'secondary',
      }
    }

    if (comparison === 'downgrade' && !isCanceled && !this._entities.subscriptionStore.isTrial) {
      return {
        text: 'Downgrade plan',
        intent: 'destructive',
        contained: 'secondary',
      }
    }

    return {
      text: 'Select plan',
      contained: isPro ? 'primary' : 'secondary',
    }
  }

  get plans(): IPlan[] {
    return [
      {
        name: 'Basic',
        icon: 'cube',
        description: 'For small companies that want to text their customers at scale.',
        selectItems: this.basicPlansDropdownItems,
        onSelectValue: (value) => {
          this.setSelectedValue('Basic', String(value))
        },
        buttonProps: {
          onClick: () => {
            this.handleSelectPlan('Basic')
          },
          ...this.getButtonPropsForPlan(this.selectedBasicPlan, false),
        },
        iconColor: 'var(--green-70)',
        showTooltip: true,
        price: this._planSelectionStore.formatPrice(this.selectedBasicPlan),
        suffix: this._planSelectionStore.getPriceSuffix(this.selectedBasicPlan),
      },
      {
        name: 'Pro',
        icon: 'atom',
        description: 'For teams that deliver best-in-class service through texting and calling.',
        selectItems: this.proPlansDropdownItems,
        onSelectValue: (value) => {
          this.setSelectedValue('Pro', String(value))
        },
        buttonProps: {
          onClick: () => {
            this.handleSelectPlan('Pro')
          },
          ...this.getButtonPropsForPlan(this.selectedProPlan, true),
        },
        iconColor: 'var(--content-brand-primary)',
        showTooltip: true,
        price: this._planSelectionStore.formatPrice(this.selectedProPlan),
        suffix: this._planSelectionStore.getPriceSuffix(this.selectedProPlan),
      },
      {
        name: 'Enterprise',
        icon: 'diamond',
        description: 'For businesses where texting is a vital part of operations.',
        selectItems: [
          { id: 'monthly', label: 'Monthly' },
          { id: 'yearly', label: 'Yearly' },
        ],
        onSelectValue: () => {},
        buttonProps: {
          id: 'intercom-custom-plan_modal-button',
          text: 'Chat with sales',
          contained: 'secondary',
          onClick: this.chatWithSalesHandler,
        },
        iconColor: 'var(--pink-70)',
        showTooltip: false,
        showCustomCredits: true,
        isCustom: true,
        price: 'Contact for pricing',
        suffix: null,
      },
    ]
  }

  get showAutoRechargeBlock() {
    if (this._entities.subscriptionStore.isTrial) return false

    return this._showAutoRechargeBlock
  }

  get showPhoneCheckrBlock() {
    return this._showPhoneCheckrBlock
  }

  get subscriptionSuggestion() {
    return this._entities.subscriptionStore.subscriptionSuggestion
  }

  get activeSuggestedPlan() {
    return (
      this._entities.billingStore.billingPlansStore.getItem(
        this.subscriptionSuggestion?.suggested_plan ||
          this.subscriptionSuggestion?.suggested_annual_plan ||
          ''
      ) || null
    )
  }

  get activeYearlySuggestedPlan() {
    const subscriptionSuggestion = this.subscriptionSuggestion
    if (!subscriptionSuggestion) return null

    const suggestedPlanId = subscriptionSuggestion.suggested_annual_plan
    if (!suggestedPlanId) return null

    return this._entities.billingStore.billingPlansStore.getItem(suggestedPlanId) || null
  }

  get suggestedPlan() {
    const subscriptionSuggestion = this.subscriptionSuggestion
    if (!subscriptionSuggestion) return null

    const suggestedPlanId =
      subscriptionSuggestion.suggested_annual_plan || subscriptionSuggestion.suggested_plan
    if (!suggestedPlanId) return null
    return this._entities.billingStore.billingPlansStore.getItem(suggestedPlanId) || null
  }

  private get baseCreditsUsed() {
    const subscriptionSuggestion = this.subscriptionSuggestion
    if (!subscriptionSuggestion) return 0

    const boughtCreditsAmount = subscriptionSuggestion.bought_credits_amount || 0
    const planCredits = this.subscriptionPlanCredits || 0

    return boughtCreditsAmount + planCredits
  }

  private get isCurrentPlanNotYearlyAndPeriodYearly() {
    const subscriptionSuggestion = this.subscriptionSuggestion
    if (!subscriptionSuggestion) return false

    const isCurrentPlanNotYearly = subscriptionSuggestion.current_plan_period !== 'yearly'
    const isPeriodYearly = this.period === 'annually'

    return isCurrentPlanNotYearly && isPeriodYearly
  }

  private calculatePlanTotalPrice = (
    plan: BillingPlan | null | undefined,
    creditsMultiplier = 1
  ): number => {
    if (!plan) return 0

    const totalCreditsUsed = this.baseCreditsUsed * creditsMultiplier
    const extraCredits = Math.max(0, totalCreditsUsed - plan.credits)
    const additionalCreditAmount = plan.origin.additional_credit_amount || 0
    const additionalCreditAmountFormatted = additionalCreditAmount / 100
    const extraPrice = Math.ceil(extraCredits * additionalCreditAmountFormatted)
    const basePrice = plan.price_dollars || 0

    return Math.ceil(basePrice + extraPrice)
  }

  get suggestedPlanTotalPrice() {
    return this.calculatePlanTotalPrice(this.activeSuggestedPlan)
  }

  get suggestedYearlyPlanTotalPrice() {
    if (!this.isCurrentPlanNotYearlyAndPeriodYearly) return 0

    return this.calculatePlanTotalPrice(this.activeYearlySuggestedPlan, 12)
  }

  get isPlanLoading() {
    return this._planLoading
  }

  get shouldShowPeriodTabs(): boolean {
    const { subscriptionStore } = this._entities

    if (subscriptionStore.isTrial || subscriptionStore.isCanceled) {
      return true
    }

    const currentSubscriptionPlanId = this.subscriptionPlan?.id
    const selectedPlan = this._selectedPlan
    const selectedPlanType = this._selectedPlanType

    if (!currentSubscriptionPlanId || !selectedPlan || !selectedPlanType) {
      return true
    }

    const isSelectedMonthly = selectedPlan.interval === 'monthly'
    const currentPeriod: BillingPeriod = isSelectedMonthly ? 'monthly' : 'annually'
    const otherPeriod: BillingPeriod = isSelectedMonthly ? 'annually' : 'monthly'

    const currentPeriodPlans =
      selectedPlanType === 'Basic'
        ? this.getBasicPlans(currentPeriod)
        : this.getProPlans(currentPeriod)
    const otherPeriodPlans =
      selectedPlanType === 'Basic' ? this.getBasicPlans(otherPeriod) : this.getProPlans(otherPeriod)

    const selectedPlanIndex = this.findPlanIndexById(currentPeriodPlans, selectedPlan.id)
    const equivalentPlanId = this.getPlanIdByIndex(otherPeriodPlans, selectedPlanIndex)

    return equivalentPlanId !== currentSubscriptionPlanId
  }

  get upgradeSaveCost() {
    const subscriptionSuggestion = this.subscriptionSuggestion
    const suggestedPlan = this.suggestedPlan
    const isCustomPlan = this.subscriptionPlan?.custom || false

    if (isCustomPlan || !suggestedPlan) return 0

    const roundedPlanPrice = isCustomPlan
      ? Math.round(this.subscriptionPlan?.price || 0) / 100
      : Math.round(this.subscriptionPlan?.price || 0)
    const ceilOverageCost = Math.ceil(subscriptionSuggestion?.bought_credits_cost || 0)
    const currentPlanTotalPrice = Math.ceil(roundedPlanPrice + ceilOverageCost)

    if (this.isCurrentPlanNotYearlyAndPeriodYearly) {
      return currentPlanTotalPrice * 12 - this.suggestedYearlyPlanTotalPrice
    }

    return currentPlanTotalPrice - this.suggestedPlanTotalPrice
  }

  sendUpdateAnalyticsEvent = () => {
    const { eventLogStore } = this._entities
    const { organizationStore, usersStore } = this._entities
    const eventData: Record<
      string,
      | string
      | number
      | undefined
      | null
      | boolean
      | Record<string, string | number | undefined | null | boolean>
    > = {
      event_id: 'upgrade_modal_used',
      action: 'Upgrade Request Sent',
      agencyId: organizationStore.agency_id,
      userId: usersStore.user?.id,
      'Modal Version': 'V2.1',
      Change_Type: 'Upgrade',
      Plan: this.selectedPlanInfo?.name,
      'Additional Seats': this.seats,
      'Additional Numbers': this.numbers,
      'Auto-Recharge': !this._showAutoRechargeBlock
        ? 'HIDDEN'
        : this._entities.billingStore.autorecharge?.is_active,
      'Phone Checkr': !this._showPhoneCheckrBlock
        ? 'HIDDEN'
        : organizationStore.isPhoneCheckerEnabled,

      context: {
        groupId: organizationStore.id,
      },
    }

    if (this.selectedPlan?.interval === 'monthly' && this._selectedPlan && this._planData) {
      eventData['Plan MRR'] = this._selectedPlan.price_dollars
      eventData['Add-ons MRR'] = (this._planData.next_charge_details?.mrr ?? 0) / 100
    }

    if (this.selectedPlan?.interval === 'annual' && this._selectedPlan && this._planData) {
      eventData['Plan ARR'] = this._selectedPlan.price_dollars
      eventData['Add-ons ARR'] = (this._planData.next_charge_details?.arr ?? 0) / 100
    }

    eventLogStore.logEvent('Upgrade Modal Used', eventData, { groupId: organizationStore.id })
  }

  sendDowngradeAnalyticsEvent = () => {
    const { eventLogStore } = this._entities
    const { organizationStore, usersStore } = this._entities
    const eventData: Record<
      string,
      | string
      | number
      | undefined
      | null
      | boolean
      | Record<string, string | number | undefined | null | boolean>
    > = {
      event_id: 'upgrade_modal_used',
      action: 'Downgrade Request Sent',
      agencyId: organizationStore.agency_id,
      userId: usersStore.user?.id,
      'Modal Version': 'V2.1',
      Change_Type: 'Downgrade',
      'Downgrade Reason': this.downgradeContentStore.selectedReason,
      'Downgrade Additional Info': this.downgradeContentStore.reasonDesc,
      Plan: this.selectedPlanInfo?.name,

      context: {
        groupId: organizationStore.id,
      },
    }

    if (this.selectedPlan?.interval === 'monthly') {
      eventData['Plan MRR'] = this._selectedPlan?.price_dollars || 0
    }

    if (this.selectedPlan?.interval === 'annual') {
      eventData['Plan ARR'] = this._selectedPlan?.price_dollars || 0
    }

    eventLogStore.logEvent('Upgrade Modal Used', eventData, { groupId: organizationStore.id })
  }

  checkAddOnsShowing = () => {
    const { organizationStore, billingStore } = this._entities
    const { isPhoneCheckerEnabled } = organizationStore
    const { autorecharge } = billingStore
    const isAutoRechargeActive = autorecharge?.is_active

    this._showAutoRechargeBlock = !isAutoRechargeActive
    this._showPhoneCheckrBlock = !isPhoneCheckerEnabled
  }

  loadPlanData = async () => {
    if (!this.selectedPlanInfo?.id) return

    try {
      this._planLoading = true
      const { data } = await BillingApi.getPlanData({
        params: {
          plan: this.selectedPlanInfo?.id || '',
          seats: this.seats,
          numbers: this.numbers,
        },
      })

      runInAction(() => {
        this._planData = data
      })
    } catch (error) {
      logger.error(error)
    } finally {
      runInAction(() => {
        this._planLoading = false
      })
    }
  }

  clearPaymentError = () => {
    this._paymentError = false
    this._dropdownCardsStore.clearPaymentError()
  }

  handleSelectPlan = (planType: 'Basic' | 'Pro') => {
    this._selectedPlanType = planType

    const selectedPlanId = this.selectedValues[planType]
    const plans =
      planType === 'Basic' ? this.getBasicPlans(this.period) : this.getProPlans(this.period)
    this._selectedPlan = plans.find((plan) => plan.id === selectedPlanId) || null

    this.nextStep()
    this.loadPlanData()
  }

  getBasicPlans = (interval: BillingPeriod) => {
    return this._planSelectionStore.getBasicPlans(interval)
  }

  getProPlans = (interval: BillingPeriod) => {
    return this._planSelectionStore.getProPlans(interval)
  }

  convertPlansToDropdownItems = (
    plans: BillingPlan[],
    selectedPlan: BillingPlan | null
  ): IDropdownItem[] => {
    return this._planSelectionStore.convertPlansToDropdownItems(plans, selectedPlan)
  }

  getPlansList = (interval: BillingPeriod) => {
    return this._planSelectionStore.getPlansList(interval)
  }

  setDefaultValues = () => {
    const { subscriptionStore } = this._entities

    const planCredits = this.subscriptionPlanCredits
    const subscriptionPlan = this.subscriptionPlan
    let basicPlansList = this.getBasicPlans(this.period)
    let proPlansList = this.getProPlans(this.period)

    if (basicPlansList.length === 0 || proPlansList.length === 0) {
      return
    }

    const shouldSetFirstPlans =
      subscriptionStore.isTrial ||
      subscriptionStore.status === 'canceled' ||
      subscriptionStore.isPAYGPlan

    if (shouldSetFirstPlans) {
      this.setFirstPlans(basicPlansList, proPlansList)
      return
    }

    if (subscriptionPlan?.custom) {
      this.setLastPlans(basicPlansList, proPlansList)
      return
    }

    const suggestedPlan = this.suggestedPlan

    if (suggestedPlan) {
      basicPlansList = this.getBasicPlans(this.period)
      proPlansList = this.getProPlans(this.period)

      const isBasicPlan = basicPlansList.some((plan) => plan.id === suggestedPlan.id)
      const isProPlan = proPlansList.some((plan) => plan.id === suggestedPlan.id)

      if (isBasicPlan) {
        this._planSelectionStore.setSelectedValue('Basic', suggestedPlan.id)
        this._selectedPlanType = 'Basic'
        this._selectedPlan = suggestedPlan

        if (proPlansList.length) {
          this._planSelectionStore.setSelectedValue('Pro', proPlansList[0].id)
        }
      }

      if (isProPlan) {
        this._planSelectionStore.setSelectedValue('Pro', suggestedPlan.id)
        this._selectedPlanType = 'Pro'
        this._selectedPlan = suggestedPlan

        if (basicPlansList.length) {
          this._planSelectionStore.setSelectedValue(
            'Basic',
            basicPlansList[basicPlansList.length - 1].id
          )
        }
      }

      return
    }

    if (subscriptionPlan?.interval === 'monthly' && planCredits) {
      const monthlyBasicPlans = this.getBasicPlans('monthly')
      const monthlyProPlans = this.getProPlans('monthly')

      const monthlyBasicIndex = this.findPlanIndexByCredits(monthlyBasicPlans, planCredits)
      if (monthlyBasicIndex !== -1 && basicPlansList[monthlyBasicIndex]) {
        this.handleBasicPlanFound(basicPlansList, proPlansList, monthlyBasicIndex)
        return
      }

      const monthlyProIndex = this.findPlanIndexByCredits(monthlyProPlans, planCredits)
      if (monthlyProIndex !== -1 && proPlansList[monthlyProIndex]) {
        this.handleProPlanFound(basicPlansList, proPlansList, monthlyProIndex)
        return
      }
    }

    if (!planCredits) {
      this.setLastPlans(basicPlansList, proPlansList)
      return
    }

    const basicPlanIndex = this.findPlanIndexByCredits(basicPlansList, planCredits)
    if (basicPlanIndex !== -1) {
      this.handleBasicPlanFound(basicPlansList, proPlansList, basicPlanIndex)
      return
    }

    const proPlanIndex = this.findPlanIndexByCredits(proPlansList, planCredits)
    if (proPlanIndex !== -1) {
      this.handleProPlanFound(basicPlansList, proPlansList, proPlanIndex)
      return
    }

    this.setLastPlans(basicPlansList, proPlansList)
  }

  private findPlanIndexByCredits = (plans: BillingPlan[], credits: number): number => {
    return plans.findIndex((plan) => plan.credits === credits)
  }

  private setLastPlans = (basicPlans: BillingPlan[], proPlans: BillingPlan[]) => {
    if (basicPlans.length) {
      this._planSelectionStore.setSelectedValue('Basic', basicPlans[basicPlans.length - 1].id)
    }
    if (proPlans.length) {
      this._planSelectionStore.setSelectedValue('Pro', proPlans[proPlans.length - 1].id)
    }
  }

  private setFirstPlans = (basicPlans: BillingPlan[], proPlans: BillingPlan[]) => {
    if (basicPlans.length) {
      this._planSelectionStore.setSelectedValue('Basic', basicPlans[0].id)
    }
    if (proPlans.length) {
      this._planSelectionStore.setSelectedValue('Pro', proPlans[0].id)
    }
  }

  private handleBasicPlanFound = (
    basicPlans: BillingPlan[],
    proPlans: BillingPlan[],
    index: number
  ) => {
    const isLastBasicPlan = index === basicPlans.length - 1

    if (isLastBasicPlan) {
      if (basicPlans[index]) {
        this._planSelectionStore.setSelectedValue('Basic', basicPlans[index].id)
      }
      if (proPlans.length) {
        this._planSelectionStore.setSelectedValue('Pro', proPlans[0].id)
      }
    } else {
      if (basicPlans[index + 1]) {
        this._planSelectionStore.setSelectedValue('Basic', basicPlans[index + 1].id)
      }
    }
  }

  private handleProPlanFound = (
    basicPlans: BillingPlan[],
    proPlans: BillingPlan[],
    index: number
  ) => {
    if (basicPlans.length) {
      this._planSelectionStore.setSelectedValue('Basic', basicPlans[basicPlans.length - 1].id)
    }
    const isLastProPlan = index === proPlans.length - 1
    if (isLastProPlan) {
      if (proPlans[index]) {
        this._planSelectionStore.setSelectedValue('Pro', proPlans[index].id)
      }
    } else {
      if (proPlans[index + 1]) {
        this._planSelectionStore.setSelectedValue('Pro', proPlans[index + 1].id)
      }
    }
  }

  nextStep = () => {
    if (this._currentStep < this._totalSteps) {
      this._currentStep += 1
    }
  }

  previousStep = () => {
    if (this._currentStep > 1) {
      this._currentStep -= 1
    }
  }

  setPeriod = (period: BillingPeriod) => {
    this._planSelectionStore.setPeriod(period)
  }

  handleSetPeriod = (tab: ITabItem<BillingPeriod>) => {
    this._planSelectionStore.handleSetPeriod(tab)
    const period = this.period
    if (this._selectedPlanType) {
      const newSelectedPlanId = this.selectedValues[this._selectedPlanType]
      const plans =
        this._selectedPlanType === 'Basic' ? this.getBasicPlans(period) : this.getProPlans(period)
      this._selectedPlan = plans.find((plan) => plan.id === newSelectedPlanId) || null
    }
    if (this._planData) {
      const isMonthly = period === 'monthly'
      this._planData.seats.unit_price = (isMonthly ? seatsPrice : seatsPriceYearly) * 100
      this._planData.numbers.unit_price = (isMonthly ? numbersPrice : numbersPriceYearly) * 100
    }
    this.loadPlanData()
  }

  applySelectedPeriodSuggestedPlan = () => {
    const userSelectedPeriod = this.period

    const suggestedPlanId =
      userSelectedPeriod === 'monthly'
        ? this.subscriptionSuggestion?.suggested_plan
        : this.subscriptionSuggestion?.suggested_annual_plan ||
          this.subscriptionSuggestion?.suggested_plan

    if (!suggestedPlanId) {
      this.setDefaultValues()
      return
    }

    const suggestedPlan =
      this._entities.billingStore.billingPlansStore.getItem(suggestedPlanId) || null

    if (!suggestedPlan) {
      this.setDefaultValues()
      return
    }

    const basicPlansList = this.getBasicPlans(userSelectedPeriod)
    const proPlansList = this.getProPlans(userSelectedPeriod)

    const isBasicPlan = basicPlansList.some((plan) => plan.id === suggestedPlan.id)
    const isProPlan = proPlansList.some((plan) => plan.id === suggestedPlan.id)

    if (isBasicPlan) {
      this._planSelectionStore.setSelectedValue('Basic', suggestedPlan.id)
      this._selectedPlanType = 'Basic'
      this._selectedPlan = suggestedPlan

      if (proPlansList.length) {
        this._planSelectionStore.setSelectedValue('Pro', proPlansList[0].id)
      }
    } else if (isProPlan) {
      this._planSelectionStore.setSelectedValue('Pro', suggestedPlan.id)
      this._selectedPlanType = 'Pro'
      this._selectedPlan = suggestedPlan

      if (basicPlansList.length) {
        this._planSelectionStore.setSelectedValue(
          'Basic',
          basicPlansList[basicPlansList.length - 1].id
        )
      }
    } else {
      this.setDefaultValues()
    }
  }

  private findPlanIndexById = (plans: BillingPlan[], planId: string | undefined): number => {
    return this._planSelectionStore.findPlanIndexById(plans, planId)
  }

  private getPlanIdByIndex = (plans: BillingPlan[], index: number): string => {
    return this._planSelectionStore.getPlanIdByIndex(plans, index)
  }

  setSelectedValue = (planName: string, value: string) => {
    this._planSelectionStore.setSelectedValue(planName, value)
  }

  changeCounter = (type: 'seats' | 'numbers', value: number) => {
    const clampedValue = Math.max(0, value)
    if (type === 'seats') {
      this._seats = clampedValue
    } else {
      this._numbers = clampedValue
    }
  }

  changeSeatsCounter = (value: number) => {
    this.changeCounter('seats', value)
    this._debouncedLoadPlanData()
  }

  changeNumbersCounter = (value: number) => {
    this.changeCounter('numbers', value)
    this._debouncedLoadPlanData()
  }

  openAutoRechargeModal = () => {
    this._autoRechargeWidgetStore.openModal()
  }

  afterUpgradePlan = (isDowngrade?: boolean) => {
    const wasTrialUpgrade = this._entities.subscriptionStore.isTrial

    if (isDowngrade) {
      this.sendDowngradeAnalyticsEvent()
    } else {
      this.sendUpdateAnalyticsEvent()
    }

    uiStore.changeRouteViaVue({
      path: `/${BillingPath.overview}`,
    })

    const planType = this._selectedPlanType || 'Plan'
    const planName =
      this.selectedPlanInfo?.name || this._selectedPlan?.labelCreditsByIntervalAndPlanType || 'plan'
    const firstName = this._entities.usersStore.user?.first_name || ''
    const title = firstName ? `Welcome to ${planType}, ${firstName}!` : `Welcome to ${planType}!`
    const desc = isDowngrade
      ? `You successfully downgraded to ${planName} plan`
      : `You successfully upgraded to ${planName} plan${
          wasTrialUpgrade ? '. Your trial inbox is removed.' : ''
        }`

    const openQuestionnaireModal = () => {
      modalStore.removeModal('upgradePlanModal')

      if (wasTrialUpgrade || this._entities.usersStore.user?.isRequireNumber) {
        new QuestionnaireOrSelectNumberModalStore(this._entities).init()
      }
    }

    setTimeout(() => {
      modalStore.addModal({
        id: 'upgradePlanModal',
        type: ModalTypeList.SUCCESS,
        title,
        desc,
        showCloseButton: false,
        primaryAction: {
          text: "Let's go",
          onAction: () => {
            modalStore.closeModal('upgradePlanModal')
          },
        },
        onClose: openQuestionnaireModal,
      })
    }, 100)
  }

  upgradePlan = async (isDowngrade?: boolean) => {
    try {
      this.clearPaymentError()
      this._isUpgrading = true

      const isCanceled = this._entities.subscriptionStore.isCanceled

      if (isCanceled) {
        await BillingApi.restartSubscription({
          plan: this._selectedPlan?.id || '',
          payment_method: this._dropdownCardsStore.activeId || '',
          seats: this.seats,
          numbers: this.numbers,
        })
      } else {
        await BillingApi.upgradePlan({
          plan: this._selectedPlan?.id || '',
          seats: this.seats,
          numbers: this.numbers,
          payment_method: this._dropdownCardsStore.activeId || '',
        })
      }

      if (window.publicApiVue) {
        await window.publicApiVue.fetchMe()
      }

      await Promise.all([
        this._entities.subscriptionStore.init(),
        this._entities.organizationStore.init(),
      ])

      this.afterUpgradePlan(isDowngrade)
    } catch (e) {
      logger.error(e)
      if (e instanceof AxiosError) {
        if (
          e.errors.getError('message').startsWith('Your card ') ||
          e.errors.getError('error').startsWith('Your card ') ||
          e.errors.getError('card_error')
        ) {
          this._paymentError = true
          this._dropdownCardsStore.setPaymentError(
            e.errors.getError('card_error') || 'Please select a different card'
          )
        }
      }
    } finally {
      this._isUpgrading = false
    }
  }

  handleKeepCurrentPlan = () => {
    modalStore.closeModal('opportunityModal')

    if (!this._entities.featureFlagsStore.billing_overview_v2) {
      uiStore.changeRouteViaVue({
        path: `/${BillingPath.overview}`,
      })
    }
  }

  chatWithSalesHandler = () => {
    openIntercom()
    this.sendUpgradeOpportunityEvent('Chat with Sales')
  }

  private sendUpgradeOpportunityEvent = (action: string) => {
    if (!this.subscriptionSuggestion) return

    sendGTMEvent({
      eventName: 'Flag_Upgrade_Opportunity',
      eventParams: {
        event_id: 'flag_upgrade_opportunity',
        plan_type: this.subscriptionPlan?.id || '',
        'MRR/ARR': this.upgradeSaveCost,
        action,
      },
    })
  }
}
